import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";

import {
  useAddTodoMutation,
  useGetTodosQuery,
  useDeleteTodoMutation,
} from "../../redux/slices/productApiSlice";

import {
  useAddsTodoMutation,
  useGetsTodosQuery,
  useDeleteeTodoMutation,
} from "../../redux/slices/todoApiSlice";

import { useNavigate } from "react-router-dom";

import {
  FaRegHeart,
  FaHeart,
  FaExclamationTriangle,
  FaShoppingCart,
  FaSyncAlt,
  FaCheckCircle,
  FaTrash,
  FaBoxOpen,
  FaArrowRight,
} from "react-icons/fa";

import { toast } from "react-toastify";

const Catos = ({ catagory }) => {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [error, setError] = useState("");

  // =========================================================
  // CART
  // =========================================================

  const [addingProductId, setAddingProductId] = useState(null);

  const [addTodo] = useAddTodoMutation();
  const [deleteTodo] = useDeleteTodoMutation();

  const { data: cartData } = useGetTodosQuery();

  // =========================================================
  // FAVORITE
  // =========================================================

  const [addingFavoriteId, setAddingFavoriteId] = useState(null);

  const [addsTodo] = useAddsTodoMutation();
  const [deleteeTodo] = useDeleteeTodoMutation();

  const { data: favoriteData } = useGetsTodosQuery();

  const [favoriteIds, setFavoriteIds] = useState([]);

  const navigate = useNavigate();

  const { userInfo } = useSelector(
    (state) => state.auth
  );

  // =========================================================
  // ERROR MESSAGE
  // =========================================================

  const getErrorMessage = (err) => {
    const status =
      err?.response?.status ||
      err?.status;

    const serverMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.response?.data?.data?.message ||
      err?.data?.message ||
      err?.data?.error ||
      err?.message;

    if (serverMessage) {
      if (typeof serverMessage === "string") {
        return serverMessage;
      }

      try {
        return JSON.stringify(serverMessage);
      } catch {
        return "Serverdən naməlum xəta cavabı alındı.";
      }
    }

    if (status === 400) {
      return "Göndərilən məlumatlar düzgün deyil.";
    }

    if (status === 401) {
      return "Bu əməliyyatı yerinə yetirmək üçün hesabınıza daxil olmalısınız.";
    }

    if (status === 403) {
      return "Bu əməliyyatı yerinə yetirmək üçün icazəniz yoxdur.";
    }

    if (status === 404) {
      return "Sorğu edilən məlumat tapılmadı.";
    }

    if (status === 409) {
      return "Bu məhsul artıq mövcuddur.";
    }

    if (status === 429) {
      return "Çox sayda sorğu göndərildi. Bir neçə saniyə gözləyib yenidən cəhd edin.";
    }

    if (status >= 500) {
      return "Serverdə texniki problem baş verdi. Zəhmət olmasa, bir qədər sonra yenidən cəhd edin.";
    }

    if (
      err?.code === "ERR_NETWORK" ||
      err?.message
        ?.toLowerCase()
        ?.includes("network")
    ) {
      return "Serverlə əlaqə yaratmaq mümkün olmadı. İnternet bağlantınızı yoxlayın.";
    }

    return "Naməlum xəta baş verdi. Zəhmət olmasa, yenidən cəhd edin.";
  };

  // =========================================================
  // DUPLICATE FAVORITE CHECK
  // =========================================================

  const isAlreadyFavoriteError = (err) => {
    const status =
      err?.response?.status ||
      err?.status;

    const message = String(
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.data?.message ||
      err?.data?.error ||
      err?.message ||
      ""
    ).toLowerCase();

    return (
      status === 409 ||
      message.includes("already") ||
      message.includes("exist") ||
      message.includes("duplicate") ||
      message.includes("artıq") ||
      message.includes("artiq") ||
      message.includes("mövcuddur") ||
      message.includes("movcuddur") ||
      message.includes("favor") ||
      message.includes("sevimli")
    );
  };

  // =========================================================
  // CART DATA
  // =========================================================

  const cartItems =
    cartData?.todos ||
    cartData?.allTodos ||
    cartData?.products ||
    cartData ||
    [];

  const getCartProductId = (item) => {
    const productId =
      item?.productId?._id ||
      item?.productId ||
      item?.product?._id ||
      item?.qolbaq?._id ||
      item?.qolbaqId?._id ||
      item?.qolbaqId ||
      item?.qolbaq_id;

    return productId
      ? String(productId)
      : null;
  };

  const isProductInCart = (productId) => {
    if (
      !Array.isArray(cartItems) ||
      !productId
    ) {
      return false;
    }

    return cartItems.some(
      (item) =>
        getCartProductId(item) ===
        String(productId)
    );
  };

  const getCartItem = (productId) => {
    if (
      !Array.isArray(cartItems) ||
      !productId
    ) {
      return null;
    }

    return (
      cartItems.find(
        (item) =>
          getCartProductId(item) ===
          String(productId)
      ) || null
    );
  };

  // =========================================================
  // FAVORITE DATA
  // =========================================================

  useEffect(() => {
    if (!Array.isArray(favoriteData)) {
      return;
    }

    const ids = favoriteData
      .map((item) => {
        return (
          item?.productId?._id ||
          item?.productId ||
          item?.product?._id ||
          item?.qolbaq?._id ||
          item?.qolbaqId?._id
        );
      })
      .filter(Boolean)
      .map((id) => String(id));

    setFavoriteIds(ids);
  }, [favoriteData]);

  // =========================================================
  // ADD / REMOVE CART
  // =========================================================

  const handleAddToCart = async (product) => {
    if (!product?._id) {
      toast.error(
        "Məhsulun identifikasiya məlumatı tapılmadı."
      );
      return;
    }

    const productId = String(product._id);

    const productTitle =
      product?.title || "Məhsul";

    const isInCart =
      isProductInCart(productId);

    if (addingProductId !== null) {
      return;
    }

    // =======================================================
    // REMOVE FROM CART
    // =======================================================

    if (isInCart) {
      const cartItem =
        getCartItem(productId);

      if (!cartItem?._id) {
        toast.error(
          `"${productTitle}" məhsulunun səbət məlumatı tapılmadı. Məhsul səbətdən silinə bilmədi.`
        );

        return;
      }

      try {
        setAddingProductId(productId);

        await deleteTodo(
          cartItem._id
        ).unwrap();

        toast.success(
          `"${productTitle}" məhsulu səbətdən silindi.`
        );
      } catch (err) {
        console.error(
          "Məhsul səbətdən silinərkən xəta:",
          err
        );

        toast.error(
          `"${productTitle}" məhsulunu səbətdən silmək mümkün olmadı. Səbəb: ${getErrorMessage(
            err
          )}`
        );
      } finally {
        setAddingProductId(null);
      }

      return;
    }

    // =======================================================
    // OUT OF STOCK
    // =======================================================

    if (Number(product.stock) <= 0) {
      toast.warning(
        `"${productTitle}" hazırda stokda olmadığı üçün səbətə əlavə edilə bilməz.`
      );

      return;
    }

    // =======================================================
    // ADD TO CART
    // =======================================================

    try {
      setAddingProductId(productId);

      await addTodo({
        productId,
      }).unwrap();

      toast.success(
        `"${productTitle}" məhsulu səbətə uğurla əlavə edildi.`
      );
    } catch (err) {
      console.error(
        "Məhsul səbətə əlavə edilərkən xəta:",
        err
      );

      toast.error(
        `"${productTitle}" məhsulunu səbətə əlavə etmək mümkün olmadı. Səbəb: ${getErrorMessage(
          err
        )}`
      );
    } finally {
      setAddingProductId(null);
    }
  };

  // =========================================================
  // ADD / REMOVE FAVORITE
  // =========================================================

  const handleAddToFavorite = async (product) => {
    if (!product?._id) {
      toast.error(
        "Məhsulun identifikasiya məlumatı tapılmadı."
      );

      return;
    }

    const productId =
      String(product._id);

    const productTitle =
      product?.title || "Məhsul";

    const isFavorite =
      favoriteIds.includes(productId);

    if (addingFavoriteId !== null) {
      return;
    }

    // =======================================================
    // REMOVE FAVORITE
    // =======================================================

    if (isFavorite) {
      const favoriteItem =
        Array.isArray(favoriteData)
          ? favoriteData.find(
              (item) => {
                const id =
                  item?.productId?._id ||
                  item?.productId ||
                  item?.product?._id ||
                  item?.qolbaq?._id ||
                  item?.qolbaqId?._id;

                return (
                  String(id) ===
                  productId
                );
              }
            )
          : null;

      if (!favoriteItem?._id) {
        toast.error(
          `"${productTitle}" məhsulunun favori məlumatı tapılmadı. Favoridən silmək mümkün olmadı.`
        );

        return;
      }

      try {
        setAddingFavoriteId(productId);

        await deleteeTodo(
          favoriteItem._id
        ).unwrap();

        setFavoriteIds((prev) =>
          prev.filter(
            (id) =>
              String(id) !==
              productId
          )
        );

        toast.success(
          `"${productTitle}" məhsulu sevimlilərdən silindi.`
        );
      } catch (err) {
        console.error(
          "Məhsul sevimlilərdən silinərkən xəta:",
          err
        );

        toast.error(
          `"${productTitle}" məhsulunu sevimlilərdən silmək mümkün olmadı. Səbəb: ${getErrorMessage(
            err
          )}`
        );
      } finally {
        setAddingFavoriteId(null);
      }

      return;
    }

    // =======================================================
    // ADD FAVORITE
    // =======================================================

    try {
      setAddingFavoriteId(productId);

      await addsTodo({
        productId,
      }).unwrap();

      setFavoriteIds((prev) => {
        if (
          prev.includes(productId)
        ) {
          return prev;
        }

        return [
          ...prev,
          productId,
        ];
      });

      toast.success(
        `"${productTitle}" məhsulu sevimlilər siyahısına əlavə edildi.`
      );
    } catch (err) {
      console.error(
        "Məhsul sevimlilərə əlavə edilərkən xəta:",
        err
      );

      if (
        isAlreadyFavoriteError(err)
      ) {
        setFavoriteIds((prev) => {
          if (
            prev.includes(productId)
          ) {
            return prev;
          }

          return [
            ...prev,
            productId,
          ];
        });
      } else {
        toast.error(
          `"${productTitle}" məhsulunu sevimlilərə əlavə etmək mümkün olmadı. Səbəb: ${getErrorMessage(
            err
          )}`
        );
      }
    } finally {
      setAddingFavoriteId(null);
    }
  };

  // =========================================================
  // FETCH PRODUCTS
  // =========================================================

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      try {
        if (page === 1) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        setError("");

        let url =
          `${process.env.REACT_APP_API_BASE_URL}/api/qolbaq`;

        if (userInfo?._id) {
          url += `/${userInfo._id}?page=${page}`;
        } else {
          url += `?page=${page}`;
        }

        const response =
          await axios.get(url);

        if (!isMounted) {
          return;
        }

        const data =
          response?.data?.allQolbaq;

        if (!Array.isArray(data)) {
          setError(
            "Məhsullar serverdən düzgün formatda qəbul edilmədi."
          );

          setHasMore(false);

          return;
        }

        const filteredProducts =
          data.filter(
            (item) =>
              item.catagory ===
              catagory
          );

        setProducts((prev) => {
          const existingIds =
            new Set(
              prev.map(
                (item) =>
                  item._id
              )
            );

          const newProducts =
            filteredProducts.filter(
              (item) =>
                !existingIds.has(
                  item._id
                )
            );

          return [
            ...prev,
            ...newProducts,
          ];
        });

        if (
          typeof response?.data
            ?.totalPages ===
          "number"
        ) {
          setHasMore(
            page <
            response.data
              .totalPages
          );
        } else {
          setHasMore(
            data.length > 0
          );
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }

        console.error(
          "Məhsullar yüklənərkən xəta:",
          err
        );

        setError(
          getErrorMessage(err)
        );

        if (page === 1) {
          setHasMore(false);
        }
      } finally {
        if (!isMounted) {
          return;
        }

        setLoading(false);
        setLoadingMore(false);
      }
    };

    if (page > 0) {
      fetchProducts();
    }

    return () => {
      isMounted = false;
    };
  }, [
    catagory,
    page,
    userInfo?._id,
  ]);

  // =========================================================
  // CATEGORY CHANGE
  // =========================================================

  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
    setError("");
  }, [catagory]);

  // =========================================================
  // LOAD MORE
  // =========================================================

  const loadMore = () => {
    if (
      hasMore &&
      !loadingMore &&
      !loading
    ) {
      setPage(
        (prev) =>
          prev + 1
      );
    }
  };

  // =========================================================
  // RETRY
  // =========================================================

  const retryLoading = () => {
    setError("");
    setPage(1);
    setProducts([]);
    setHasMore(true);
  };

  // =========================================================
  // GO PRODUCT
  // =========================================================

  const goToProduct = (productId) => {
    if (!productId) {
      toast.error(
        "Məhsulun identifikasiya məlumatı tapılmadı."
      );

      return;
    }

    navigate(
      `/product/${productId}`
    );
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (
    loading &&
    products.length === 0
  ) {
    return (
      <div className="w-full px-2 sm:px-4 md:px-6">
        <div className="flex min-h-[45vh] flex-col items-center justify-center py-16">

          <div className="relative mb-6 flex h-20 w-20 items-center justify-center">

            <div className="absolute inset-0 rounded-[26px] bg-blue-500/10 blur-xl" />

            <div className="relative flex h-16 w-16 items-center justify-center rounded-[22px] border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-[#151a22]">

              <div className="h-7 w-7 rounded-full border-[3px] border-slate-200 border-t-blue-600 animate-spin dark:border-slate-700 dark:border-t-blue-400" />

            </div>

          </div>

          <h3 className="text-base font-black text-slate-800 dark:text-white sm:text-lg">
            Məhsullar yüklənir
          </h3>

          <p className="mt-2 text-xs text-slate-400 dark:text-slate-500 sm:text-sm">
            Məhsullar hazırlanır, bir az gözləyin...
          </p>

        </div>
      </div>
    );
  }

  // =========================================================
  // MAIN
  // =========================================================

  return (
    <div className="w-full px-2 sm:px-4 md:px-6">

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (
        <div className="mb-5 overflow-hidden rounded-[22px] border border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/20">

          <div className="flex items-center gap-3 p-4 sm:p-5">

            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
              <FaExclamationTriangle size={17} />
            </div>

            <div className="min-w-0 flex-1">

              <h3 className="text-sm font-black text-red-800 dark:text-red-300 sm:text-base">
                Xəta baş verdi
              </h3>

              <p className="mt-1 break-words text-xs leading-5 text-red-700 dark:text-red-400 sm:text-sm">
                {error}
              </p>

            </div>

            <button
              type="button"
              onClick={retryLoading}
              className="flex h-10 shrink-0 items-center gap-2 rounded-xl bg-red-600 px-3 text-xs font-bold text-white transition hover:bg-red-700 sm:px-4"
            >
              <FaSyncAlt size={12} />

              <span className="hidden sm:inline">
                Yenidən cəhd et
              </span>
            </button>

          </div>
        </div>
      )}

      {/* =====================================================
          PRODUCT GRID
      ===================================================== */}

      {products.length > 0 ? (

        <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-4 lg:gap-4 xl:grid-cols-5">

          {products.map((product) => {

            const productId =
              String(
                product._id
              );

            const isAdding =
              addingProductId ===
              productId;

            const isAddingFavorite =
              addingFavoriteId ===
              productId;

            const isFavorite =
              favoriteIds.includes(
                productId
              );

            const isInCart =
              isProductInCart(
                productId
              );

            const isOutOfStock =
              Number(
                product.stock
              ) <= 0;

            const image =
              Array.isArray(
                product.photo
              )
                ? product.photo[0]
                : product.photo;

            return (
              <div
                key={product._id}
                className={`
                  group
                  relative
                  min-w-0
                  overflow-hidden
                  rounded-[22px]
                  border
                  bg-white
                  shadow-[0_5px_25px_rgba(15,23,42,0.05)]
                  transition-all
                  duration-300
                  dark:bg-[#11151c]

                  ${
                    isInCart
                      ? "border-emerald-300 dark:border-emerald-800"
                      : "border-slate-200 dark:border-slate-800"
                  }

                  hover:-translate-y-1
                  hover:shadow-[0_15px_35px_rgba(15,23,42,0.10)]
                `}
              >

                {/* =================================================
                    IMAGE AREA
                ================================================= */}

                <div className="relative aspect-[0.92] w-full overflow-hidden bg-slate-100 dark:bg-slate-900">

                  {image ? (
                    <img
                      src={image}
                      alt={
                        product.title ||
                        "Məhsul"
                      }
                      loading="lazy"
                      className="
                        h-full
                        w-full
                        cursor-pointer
                        object-cover
                        transition-transform
                        duration-500
                        group-hover:scale-[1.05]
                      "
                      onClick={() =>
                        goToProduct(
                          product._id
                        )
                      }
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-300 shadow-sm dark:bg-slate-800 dark:text-slate-600">
                        <FaBoxOpen size={24} />
                      </div>
                    </div>
                  )}

                  {/* IMAGE GRADIENT */}

                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />

                  {/* CART BADGE */}

                  {isInCart && (
                    <div className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[9px] font-black text-white shadow-lg sm:left-3 sm:top-3 sm:px-3 sm:py-1.5 sm:text-[10px]">
                      <FaCheckCircle size={10} />

                      <span>
                        Səbətdə
                      </span>
                    </div>
                  )}

                  {/* OUT OF STOCK */}

                  {isOutOfStock && (
                    <div className="absolute bottom-2 left-2 rounded-full bg-red-500/95 px-2.5 py-1 text-[9px] font-black text-white shadow-lg sm:bottom-3 sm:left-3 sm:text-[10px]">
                      Stokda yoxdur
                    </div>
                  )}

                  {/* FAVORITE */}

                  <button
                    type="button"
                    aria-label={
                      isFavorite
                        ? "Sevimlilərdən çıxar"
                        : "Sevimlilərə əlavə et"
                    }
                    onClick={() =>
                      handleAddToFavorite(
                        product
                      )
                    }
                    disabled={
                      isAddingFavorite
                    }
                    className={`
                      absolute
                      right-2
                      top-2
                      z-10
                      flex
                      h-9
                      w-9
                      items-center
                      justify-center
                      rounded-full
                      border
                      border-white/60
                      bg-white/95
                      shadow-lg
                      backdrop-blur
                      transition-all
                      duration-200
                      sm:right-3
                      sm:top-3
                      sm:h-10
                      sm:w-10

                      ${
                        isFavorite
                          ? "text-red-500"
                          : "text-slate-600 hover:text-red-500 dark:text-slate-700"
                      }

                      ${
                        isAddingFavorite
                          ? "cursor-wait opacity-70"
                          : "hover:scale-105"
                      }
                    `}
                  >
                    {isAddingFavorite ? (
                      <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-red-500 animate-spin" />
                    ) : isFavorite ? (
                      <FaHeart className="text-[15px] sm:text-[17px]" />
                    ) : (
                      <FaRegHeart className="text-[15px] sm:text-[17px]" />
                    )}
                  </button>

                </div>

                {/* =================================================
                    CONTENT
                ================================================= */}

                <div className="flex flex-1 flex-col p-2.5 sm:p-3.5">

                  {/* TITLE */}

                  <h3
                    onClick={() =>
                      goToProduct(
                        product._id
                      )
                    }
                    className="
                      mb-2
                      min-h-[38px]
                      cursor-pointer
                      line-clamp-2
                      text-[11px]
                      font-bold
                      leading-[18px]
                      text-slate-800
                      transition-colors
                      hover:text-blue-600
                      dark:text-white
                      dark:hover:text-blue-400
                      sm:min-h-[44px]
                      sm:text-sm
                      sm:leading-5
                    "
                  >
                    {product.title ||
                      "Adsız məhsul"}
                  </h3>

                  {/* PRICE */}

                  <div className="mb-1.5 flex min-h-[24px] flex-wrap items-center gap-1.5">

                    {product.discountApplied ? (
                      <>
                        <span className="text-[10px] font-medium text-red-400 line-through sm:text-xs">
                          {product.originalPrice} ₼
                        </span>

                        <span className="text-sm font-black text-emerald-600 dark:text-emerald-400 sm:text-lg">
                          {product.price} ₼
                        </span>
                      </>
                    ) : (
                      <span className="text-sm font-black text-slate-900 dark:text-white sm:text-lg">
                        {product.price} ₼
                      </span>
                    )}

                  </div>

                  {/* STOCK */}

                  <div className="mb-3 flex items-center">

                    <span
                      className={`
                        rounded-full
                        px-2
                        py-1
                        text-[9px]
                        font-bold
                        sm:text-[10px]

                        ${
                          product.stock > 0
                            ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400"
                            : "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400"
                        }
                      `}
                    >
                      {product.stock > 0
                        ? `Stokda ${product.stock} ədəd`
                        : "Mövcud deyil"}
                    </span>

                  </div>

                  {/* CART BUTTON */}

                  <button
                    type="button"
                    onClick={() =>
                      handleAddToCart(
                        product
                      )
                    }
                    disabled={
                      isAdding ||
                      Boolean(
                        addingProductId
                      ) ||
                      (
                        isOutOfStock &&
                        !isInCart
                      )
                    }
                    className={`
                      mt-auto
                      flex
                      min-h-[40px]
                      w-full
                      items-center
                      justify-center
                      gap-1.5
                      rounded-xl
                      px-2
                      py-2
                      text-[10px]
                      font-black
                      transition-all
                      sm:min-h-[43px]
                      sm:gap-2
                      sm:text-xs

                      ${
                        isInCart
                          ? "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-400 dark:hover:border-red-900 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                          : isOutOfStock
                            ? "cursor-not-allowed bg-slate-200 text-slate-400 dark:bg-slate-800 dark:text-slate-600"
                            : isAdding
                              ? "cursor-wait bg-blue-400 text-white"
                              : "bg-slate-900 text-white hover:bg-blue-600 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                      }
                    `}
                  >

                    {isAdding ? (
                      <>
                        <div className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />

                        <span>
                          {isInCart
                            ? "Silinir..."
                            : "Əlavə edilir..."}
                        </span>
                      </>
                    ) : isInCart ? (
                      <>
                        <FaTrash size={11} />

                        <span>
                          Səbətdən sil
                        </span>
                      </>
                    ) : isOutOfStock ? (
                      <>
                        <FaShoppingCart size={12} />

                        <span>
                          Mövcud deyil
                        </span>
                      </>
                    ) : (
                      <>
                        <FaShoppingCart size={12} />

                        <span>
                          Səbətə əlavə et
                        </span>

                        <FaArrowRight
                          size={9}
                          className="ml-0.5 opacity-60"
                        />
                      </>
                    )}

                  </button>

                </div>
              </div>
            );
          })}

        </div>

      ) : !error ? (

        /* =====================================================
           NO PRODUCTS
        ===================================================== */

        <div className="flex min-h-[55vh] items-center justify-center px-4 py-10">

          <div className="relative w-full max-w-sm overflow-hidden rounded-[30px] border border-slate-200 bg-white px-6 py-10 text-center shadow-[0_15px_50px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-[#10141b] dark:shadow-none">

            {/* Soft decorative background */}

            <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-violet-500/10 blur-3xl" />

            <div className="pointer-events-none absolute -bottom-12 -left-12 h-32 w-32 rounded-full bg-blue-500/10 blur-3xl" />

            {/* ICON */}

            <div className="relative mx-auto mb-6 flex h-24 w-24 items-center justify-center">

              <div className="absolute inset-0 rounded-full bg-slate-100 dark:bg-slate-800/80" />

              <div className="relative flex h-[68px] w-[68px] items-center justify-center rounded-[22px] border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-[#171c25]">

                <FaBoxOpen className="text-[30px] text-slate-400 dark:text-slate-500" />

              </div>

            </div>

            {/* TITLE */}

            <h3 className="relative text-lg font-black tracking-tight text-slate-900 dark:text-white">
              Məhsul yoxdur
            </h3>

            {/* DESCRIPTION */}

            <p className="relative mx-auto mt-2 max-w-[280px] text-xs leading-5 text-slate-400 dark:text-slate-500 sm:text-sm">
              Bu kateqoriyada hazırda
              göstəriləcək məhsul yoxdur.
              Yeni məhsullar əlavə olunduqda
              burada görünəcək.
            </p>

            {/* SMALL DECORATION */}

            <div className="relative mx-auto mt-6 flex w-fit items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-bold text-slate-400 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-500">

              <span className="h-1.5 w-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />

              Məhsullar gözlənilir

              <span className="h-1.5 w-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />

            </div>

          </div>

        </div>

      ) : null}

      {/* =====================================================
          LOAD MORE
      ===================================================== */}

      {hasMore &&
        products.length > 0 &&
        !error && (
          <div className="flex justify-center px-2 pb-2 pt-6 sm:pt-8">

            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="
                group
                flex
                min-h-[44px]
                w-full
                max-w-[240px]
                items-center
                justify-center
                gap-2
                rounded-2xl
                border
                border-slate-200
                bg-white
                px-6
                py-3
                text-xs
                font-black
                text-slate-700
                shadow-sm
                transition-all

                hover:-translate-y-0.5
                hover:border-blue-200
                hover:text-blue-600
                hover:shadow-lg

                disabled:cursor-wait
                disabled:opacity-60

                dark:border-slate-800
                dark:bg-[#11151c]
                dark:text-slate-300
                dark:hover:border-blue-900
                dark:hover:text-blue-400

                sm:text-sm
              "
            >

              {loadingMore ? (
                <>
                  <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-blue-500 animate-spin" />

                  Daha çox yüklənir...
                </>
              ) : (
                <>
                  Daha çox göstər

                  <FaArrowRight
                    size={10}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </>
              )}

            </button>

          </div>
        )}

      {/* =====================================================
          END
      ===================================================== */}

      {!hasMore &&
        products.length > 0 &&
        !loadingMore &&
        !error && (
          <div className="flex items-center justify-center py-7">

            <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-bold text-slate-400 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-500 sm:text-xs">

              <FaCheckCircle className="text-emerald-500" />

              Bütün məhsullar göstərilir.

            </div>

          </div>
        )}

    </div>
  );
};

export default Catos;